import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { decodeToken } from "./auth";
import LogoutModal from "./LogoutModal";
import { authApi } from "./api";
import { authStore } from "./authStore";
import InactiveUserResponseModal from "./InactiveUserResponseModal";
import { getUserLicence } from "../../pages/licence/api";
import { extractModuleNames } from "../../constants/appModules";

export const AuthContext = createContext();
export const useAuthContext = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [user, setUserState] = useState({
    id: "",
    name: "",
    role: "",
  });
  const [msToken, setMsToken] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openInactivatedUserModal, setOpenInactivatedUserModal] =
    useState(false);
  const [isActiveUser, setIsActiveUser] = useState(true);
  const [authSessionReady, setAuthSessionReady] = useState(false);
  const [profileReady, setProfileReady] = useState(true);
  const [allowedModules, setAllowedModules] = useState([]);
  const [modulesReady, setModulesReady] = useState(false);

  const navigate = useNavigate();

  const loadLicenceModules = async (profile) => {
    if (!profile?.id) {
      setAllowedModules([]);
      return [];
    }

    try {
      const licence = profile.licenceId
        ? await getUserLicence(profile.id)
        : null;
      const moduleNames = extractModuleNames(licence);
      setAllowedModules(moduleNames);
      return moduleNames;
    } catch (error) {
      console.log("Failed to load licence modules:", error);
      setAllowedModules([]);
      return [];
    }
  };

  const setUser = (decoded) => {
    if (!decoded || typeof decoded !== "object") {
      setUserState({ id: "", name: "", role: "" });
      return;
    }
    const role = decoded?.roles?.[0] ?? decoded?.role ?? "";
    setUserState({ ...decoded, role });
  };

  const appLogin = async (credentials) => {
    const res = await authApi.appLogin(credentials);

    const logged = afterLogging(res);
    if (!logged?.isInactivate) navigate("/projects");
  };
  const msLogin = async ({ msToken }) => {
    try {
      const res = await authApi.msLogin({ msToken });

      const logged = afterLogging(res);
      if (!logged?.isInactivate) navigate("/projects");
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const askToLogout = () => {
    setOpenDeleteModal(true);
  };

  const logout = async () => {
    await authApi.logout();
    setAccessToken(null);
    setMsToken(null);
    setUser({});
    setAllowedModules([]);
    setModulesReady(false);
    setProfileReady(true);
    authStore.clear();
    setOpenDeleteModal(false);
    navigate("/");
  };

  useEffect(() => {
    const tryRefresh = async () => {
      try {
        const res = await authApi.refresh();
        afterLogging(res);
      } catch (error) {
        console.log("error after refresh: ", error);
        try {
          await logout();
        } catch {
          /* ignore */
        }
      } finally {
        setAuthSessionReady(true);
      }
    };
    tryRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run initial session restore once on mount
  }, []);

  useEffect(() => {
    if (!accessToken) {
      setProfileReady(true);
      setModulesReady(true);
      setAllowedModules([]);
      return;
    }
    let cancelled = false;
    const loadUser = async () => {
      setModulesReady(false);
      try {
        const res = await authApi.getMyData();
        console.log({ myData: res?.data });
        if (!cancelled) {
          const d = res?.data ?? {};
          const role = d.roles?.[0] ?? d.role ?? "";
          setUserState((prev) => ({ ...prev, ...d, role }));
          await loadLicenceModules({ ...d, role });
        }
      } catch (err) {
        console.log("No active session");
        if (!cancelled) {
          setAllowedModules([]);
        }
      } finally {
        if (!cancelled) {
          setProfileReady(true);
          setModulesReady(true);
        }
      }
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    import("./authStore").then(({ authStore }) => {
      authStore.registerContext(setAccessToken);
    });
  }, []);

  const afterLogging = (res) => {
    if (res?.data?.accessToken) {
      const accessToken = res?.data?.accessToken;

      setProfileReady(false);
      setAccessToken(accessToken);
      authStore.setToken(accessToken);

      const decoded = decodeToken(accessToken);
      setUser(decoded);
    } else if (!res?.data?.isActive) {
      setIsActiveUser(false);
      navigate("/");
      setOpenInactivatedUserModal(true);
      return { isInactivate: true };
    } else {
      console.log("no token or isActive res:", res);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        allowedModules,
        modulesReady,
        msLogin,
        appLogin,
        askToLogout,
        accessToken: msToken,
        setAccessToken: setMsToken,
        setMsToken,
        authSessionReady,
        authBootstrapComplete: authSessionReady && profileReady && modulesReady,
      }}
    >
      {children}
      <LogoutModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        onModalLogout={logout}
      />

      <InactiveUserResponseModal
        openModal={openInactivatedUserModal}
        setOpenModal={setOpenInactivatedUserModal}
      />
    </AuthContext.Provider>
  );
};

// import { createContext, useEffect, useState } from "react"
// import axios from "axios"
// import { setSessionCookie, getSessionCookie } from "./cookieContext"
// // import {createBrowserHistory} from "history"
// import { useLocation } from 'react-router-dom'
// import Cookies from "js-cookie";

// export const AuthContext = createContext(getSessionCookie())

// export const AuthContextProvider = ({ children }) => {
//     // const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem("user")) || null)
//     const [session, setSession] = useState(getSessionCookie())
//     useEffect(
//         () => {
//             //console.log("Event ran")
//             setSession(getSessionCookie());
//         },
//         [session]
//     );

//     // const [currentUser, setCurrentUser] = useState(null)
//     // //console.log(currentUser.username)

//     const login = async (inputs) => {
//         // const res = await axios.post("/dev/login", inputs)
//         // setCurrentUser(res.data.user_data);
//         const res = await axios.post("/dev/login", inputs)
//             .then((response) => {
//                 //console.log(response.data.user_data)
//                 setSessionCookie(response.data.user_data)
//             })
//     }

//     // //console.log(currentUser)
//     const logout = async () => {
//         await axios.post("/dev/logout")
//         // setCurrentUser(null);
//         Cookies.remove("session")

//     }

//     // useEffect(() => {
//     //     localStorage.setItem("user", JSON.stringify(currentUser))
//     // }, [currentUser]);

//     // return <AuthContext.Provider value={{ currentUser, login, logout }}>
//     return <AuthContext.Provider value={{ session, login, logout }}>
//         {children}
//     </AuthContext.Provider>;
// }
